/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';

import { useShareStore } from '@stores/share-store';
import { TenantUserType } from '@api/types';

type TenantUserStoreDataType = Omit<TenantUserType, 'user'>;

type TenantUserStoreStateType = {
  data: TenantUserStoreDataType;
  initialized: boolean;
};

const initialState = (): TenantUserStoreStateType => ({
  data: {
    id: null,
    title: '',
    disableEmailNotification: false,
    roleUid: null,
    description: '',
  },
  initialized: false,
});

export const useTenantUserStore = defineStore({
  id: 'tenantUser',
  state: initialState,
  getters: {
    id: (state) => {
      if (!state.data.id) {
        throw new Error('Tenant user id not found.');
      }
      return state.data.id;
    },
    isMember: (state) => {
      if (state.data.roleUid === null) {
        return true;
      }

      const shareStore = useShareStore();
      if (!shareStore.initialized) {
        throw new Error('Shares are not initialized.');
      }

      return state.data.roleUid === shareStore.tenantMemberRole?.uid;
    },
    isManager: (state) => {
      if (state.data.roleUid === null) {
        return false;
      }

      const shareStore = useShareStore();
      if (!shareStore.initialized) {
        throw new Error('Shares are not initialized.');
      }

      return state.data.roleUid === shareStore.tenantManagerRole?.uid;
    },
    isAdmin: (state) => {
      if (state.data.roleUid === null) {
        return false;
      }

      const shareStore = useShareStore();
      if (!shareStore.initialized) {
        throw new Error('Shares are not initialized.');
      }

      return state.data.roleUid === shareStore.tenantAdminRole?.uid;
    },
  },
  actions: {
    $reset() {
      Object.assign(this, initialState());
    },
    set(data: TenantUserStoreDataType): void {
      Object.keys(this.data).forEach((key) => {
        this.data[key] = data[key];
      });
      this.initialized = true;
    },
    setPartial(data: Partial<TenantUserStoreDataType>): void {
      Object.keys(this.data).forEach((key) => {
        if (key in data) {
          this.data[key] = data[key];
        }
      });
    },
  },
});
